import { useEffect, useRef, useCallback, useState } from 'react';
import useAsyncReducer from 'use-async-reducer';

function getUpdaterPromise(asyncUpdater) {
    if (typeof asyncUpdater === 'function') {
        return asyncUpdater();
    }
    return asyncUpdater;
}
/**
 * Returns a Symbol which can be used as a dep
 */
function useRefreshSymbol() {
    const [refreshSymbol, setRefreshSymbol] = useState(() => Symbol());
    const updateSymbol = useCallback(() => {
        setRefreshSymbol(Symbol());
    }, [setRefreshSymbol]);
    return [refreshSymbol, updateSymbol];
}
/**
 * Returns previous value, or null if first render pass
 * @param value updating value
 */
function usePrevious(value) {
    const ref = useRef(null);
    useEffect(() => {
        ref.current = value;
    }, [value]);
    return ref.current;
}
/**
 * Provides an abstraction to manage async state
 * @param asyncCreator Async method to call, create this method with `useCallback` if it uses state from the component
 * @param options
 * @returns An array of two values
 *  0. State of the async operation, an object with keys `data`, `loading`, `error`
 *  1. An object of dispatch method to update the state
 *
 * @see [https://github.com/azmenak/use-async-call/blob/master/README.md](https://github.com/azmenak/use-async-call/blob/master/README.md)
 */
function useAsyncCall(asyncCreator, options = {}) {
    const [refreshSymbol, refresh] = useRefreshSymbol();
    const previousAsyncCreator = usePrevious(asyncCreator);
    const isWaiting = (() => {
        if (!options.waitFor) {
            return false;
        }
        return !options.waitFor.every(Boolean);
    })();
    const [response, actions] = useAsyncReducer(options.initialValue);
    useEffect(() => {
        let didCancel = false;
        const callAsync = async () => {
            if (asyncCreator === previousAsyncCreator || options.dontReinitialize) {
                actions.request();
            }
            else {
                actions.initialize();
            }
            if (isWaiting) {
                return;
            }
            try {
                const data = await asyncCreator();
                if (didCancel) {
                    return;
                }
                actions.success(data);
                if (typeof options.onSuccess === 'function') {
                    options.onSuccess(data);
                }
            }
            catch (error) {
                if (didCancel) {
                    return;
                }
                actions.failure(error);
                if (typeof options.onFailure === 'function') {
                    options.onFailure(error);
                }
            }
            finally {
                if (didCancel) {
                    return;
                }
                if (typeof options.onComplete === 'function') {
                    options.onComplete();
                }
            }
        };
        callAsync();
        return () => {
            didCancel = true;
        };
    }, [asyncCreator, refreshSymbol, options.dontReinitialize, isWaiting]);
    const isUnmounted = useRef(false);
    useEffect(() => {
        return () => {
            isUnmounted.current = true;
        };
    }, []);
    const update = useCallback(async (asyncUpdater, updateOptions = {}) => {
        const updateCreatorOwner = asyncCreator;
        actions.request();
        try {
            const data = await getUpdaterPromise(asyncUpdater);
            if (updateCreatorOwner === previousAsyncCreator &&
                !isUnmounted.current) {
                actions.success(data);
            }
            if (typeof updateOptions.onSuccess === 'function') {
                updateOptions.onSuccess(data);
            }
            return data;
        }
        catch (error) {
            if (updateCreatorOwner === previousAsyncCreator &&
                !isUnmounted.current) {
                if (updateOptions.saveError) {
                    actions.failure(error);
                }
                else {
                    actions.complete();
                }
            }
            if (typeof updateOptions.onFailure === 'function') {
                updateOptions.onFailure(error);
            }
            if (updateOptions.throwError) {
                throw error;
            }
        }
        finally {
            if (typeof updateOptions.onComplete === 'function') {
                updateOptions.onComplete();
            }
        }
    }, [actions, asyncCreator, previousAsyncCreator]);
    return [response, { update, refresh, actions }];
}

export default useAsyncCall;
export { usePrevious, useRefreshSymbol };
//# sourceMappingURL=index.js.map
